import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast, Toaster } from 'react-hot-toast';
import { UserRole } from '../types/auth';
import { isValidMobileNumber } from '../services/auth';
import { useAppDispatch, useAppSelector } from '../store/hooks/reduxHooks';
import { sendOTP, verifyOTPThunk, setError, expireOTP } from '../store/slices/authSlice';

// Logo import
import Logo from '../assets/CleverPe Logos/CleverPe_White_V.svg';

// Form types
type LoginFormData = {
  phoneNumber: string;
};

type OtpFormData = {
  otp: string;
};

export const LoginPage = () => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [canResend, setCanResend] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const { loading, error, otpHash, mobileNo, isAuthenticated } = useAppSelector(state => state.auth);
  
  // If the user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from);
    }
  }, [isAuthenticated, navigate, location]);
  
  // Clear any previous errors when switching steps
  useEffect(() => {
    if (error) {
      dispatch(setError(null));
    }
  }, [step, dispatch, error]);
  
  // Form validation for phone step
  const { 
    register: registerPhone, 
    handleSubmit: handlePhoneSubmit, 
    formState: { errors: phoneErrors } 
  } = useForm<LoginFormData>({ 
    mode: 'onChange',
    defaultValues: {
      phoneNumber: ''
    }
  });
  
  // Form validation for OTP step
  const { 
    register: registerOtp, 
    handleSubmit: handleOtpSubmit,
    formState: { errors: otpErrors },
    setFocus
  } = useForm<OtpFormData>({ 
    mode: 'onChange',
    defaultValues: {
      otp: ''
    }
  });
  
  // Display toast messages when error changes
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);
  
  /**
   * Handle sending OTP
   */
  const handleSendOtp = async (data: LoginFormData) => {
    console.log('[debug] Sending OTP to:', data.phoneNumber);
    
    const result = await dispatch(sendOTP(data.phoneNumber));
    
    if (sendOTP.fulfilled.match(result)) {
      console.log('[debug] OTP sent successfully, otpHash received');
      
      // Double-check that the values are stored in sessionStorage
      const storedOtpHash = sessionStorage.getItem('otpHash');
      const storedMobileNo = sessionStorage.getItem('mobileNo');
      
      console.log('[debug] Saved to sessionStorage:', {
        mobileNo: storedMobileNo ? 'exists' : 'missing', 
        otpHash: storedOtpHash ? 'exists' : 'missing'
      });
      
      // If not stored correctly in sessionStorage, store them manually
      if (!storedOtpHash && result.payload.otpHash) {
        console.log('[debug] Manually storing otpHash in sessionStorage');
        sessionStorage.setItem('otpHash', result.payload.otpHash);
      }
      
      if (!storedMobileNo && result.payload.mobileNo) {
        console.log('[debug] Manually storing mobileNo in sessionStorage');
        sessionStorage.setItem('mobileNo', result.payload.mobileNo);
      }
      
      setStep('otp');
      setResendTimer(180); // 3 minutes in seconds
      setCanResend(false);
      toast.success('OTP sent successfully!');
    } else {
      console.error('[debug] Failed to send OTP:', result.error);
    }
  };
  
  // Timer for OTP resend functionality
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (resendTimer > 0) {
      intervalId = setInterval(() => {
        setResendTimer(prevTimer => {
          if (prevTimer <= 1) {
            setCanResend(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [resendTimer]);
  
  // Handle expiring OTP when changing steps
  useEffect(() => {
    console.log('[debug] Step changed to:', step, 'otpHash:', otpHash ? 'exists' : 'null', 'mobileNo:', mobileNo ? 'exists' : 'null');
    
    // Only expire OTP when explicitly going back to phone step from OTP step
    if (step === 'phone' && otpHash && mobileNo) {
      console.log('[debug] Expiring OTP because user went back to phone step');
      // Expire OTP only when intentionally going back to phone entry
      dispatch(expireOTP());
    }
  }, [step, dispatch, otpHash, mobileNo]);
  
  /**
   * Handle OTP verification
   */
  const handleVerifyOtp = async (data: OtpFormData) => {
    try {
      // Get mobileNo and otpHash from state or sessionStorage
      const currentMobileNo = mobileNo || sessionStorage.getItem('mobileNo');
      const currentOtpHash = otpHash || sessionStorage.getItem('otpHash');
      
      // Clean up OTP - ensure it's a string and trim any whitespace
      const cleanOtp = String(data.otp).trim();
      
      console.log('[debug] Starting OTP verification with:', {
        mobileNo: currentMobileNo ? 'exists' : 'missing',
        otpHash: currentOtpHash ? 'exists' : 'missing',
        otp: cleanOtp,
        otpLength: cleanOtp.length
      });
      
      // Debug sessionStorage
      console.log('[debug] Direct sessionStorage check:', {
        mobileNo: sessionStorage.getItem('mobileNo') ? 'exists in sessionStorage' : 'missing from sessionStorage',
        otpHash: sessionStorage.getItem('otpHash') ? 'exists in sessionStorage' : 'missing from sessionStorage'
      });
      
      if (!currentMobileNo || !currentOtpHash) {
        console.error('[debug] Missing mobileNo or otpHash');
        dispatch(setError('Session expired. Please enter your mobile number again.'));
        setStep('phone');
        return;
      }
      
      // Check OTP format - backend expects a numeric string
      if (!/^\d+$/.test(cleanOtp)) {
        dispatch(setError('OTP must contain only numbers'));
        return;
      }
      
      // Use the cleaned OTP
      const result = await dispatch(verifyOTPThunk({
        mobileNo: currentMobileNo,
        otp: cleanOtp,
        otpHash: currentOtpHash
      }));
      
      if (verifyOTPThunk.fulfilled.match(result)) {
        console.log('[debug] OTP verification successful:', result.payload);
        console.log('[debug] User permissions:', result.payload.user.dashPermissions);
        toast.success('Login successful!');
        
        // Get the user role from the response
        const user = result.payload.user;
        
        // Determine redirect path based on role
        let redirectPath = '/dashboard'; // Default fallback
        
        if (user && user.role) {
          switch (user.role) {
            case UserRole.SUPERADMIN:
              redirectPath = '/superadmin/dashboard';
              break;
            case UserRole.ADMIN:
              redirectPath = '/admin/dashboard';
              break;
            case UserRole.CLEVERPE_USER:
              redirectPath = '/cleverpeuser/dashboard';
              break;
          }
        }
        
        console.log(`[debug] Redirecting to: ${redirectPath}`);
        
        // Navigate to the appropriate dashboard
        setTimeout(() => {
          const from = location.state?.from?.pathname || redirectPath;
          navigate(from);
        }, 1000);
      } else {
        console.error('[debug] OTP verification failed:', result);
        // Do not redirect on failure, stay on the OTP page
      }
    } catch (error) {
      console.error('[debug] Error during OTP verification:', error);
      toast.error('An unexpected error occurred during verification. Please try again.');
    }
  };
  
  // Masked phone number for display
  const maskedPhone = mobileNo ? `${mobileNo.substring(0, 2)}******${mobileNo.substring(8, 10)}` : '';

  // Format timer to mm:ss
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Update the useEffect for auto-focus
  useEffect(() => {
    if (step === 'otp') {
      // Use setFocus instead of ref
      setFocus('otp');
    }
  }, [step, setFocus]);

  // Update the numeric-only input handler to be less restrictive
  const handleOtpInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow paste operations (Ctrl+V or Cmd+V)
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    // Allow only numbers and control keys (backspace, delete, arrows)
    if (!/^\d$/.test(e.key) && 
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Add paste handler to clean pasted content
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numericValue = pastedData.replace(/\D/g, '').slice(0, 6);
    const input = e.currentTarget;
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
    nativeInputValueSetter?.call(input, numericValue);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0f4ff] to-[#f9fbff] p-4">
      <Toaster position="top-center" toastOptions={{
        style: {
          border: '1px solid #E3E8FF',
          padding: '16px',
          color: '#231F20',
          fontFamily: 'Gilroy-Medium',
          fontSize: '14px',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(88, 101, 242, 0.07)'
        },
        success: {
          iconTheme: {
            primary: '#5865F2',
            secondary: '#FFFFFF',
          }
        }
      }} />

      <div className="w-full max-w-5xl flex overflow-hidden shadow-2xl rounded-2xl bg-white">
        {/* Left side - Visual part */}
        <div className="w-1/2 relative hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-br from-gradient-blue-1 to-gradient-blue-2">
            <div className="absolute inset-0 opacity-20">
              {/* Abstract pattern overlay */}
              <svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <defs>
                  <pattern id="pattern1" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="2" fill="white" fillOpacity="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#pattern1)" />
                <path d="M0,100 Q100,50 200,100 T400,100 L400,0 L0,0 Z" fill="white" fillOpacity="0.05" />
                <path d="M0,200 Q100,150 200,200 T400,200 L400,100 L0,100 Z" fill="white" fillOpacity="0.05" />
                <path d="M0,300 Q100,250 200,300 T400,300 L400,200 L0,200 Z" fill="white" fillOpacity="0.05" />
              </svg>
            </div>
            
            <div className="relative h-full flex flex-col justify-between z-10 p-6">
              <div className="flex flex-col">
                <img 
                  src={Logo} 
                  alt="CleverPe Logo" 
                  className="h-16 mb-4 filter brightness-0 invert" 
                />
                <h1 className="text-3xl font-gilroy-bold text-white mb-4 leading-tight">
                  Welcome to CleverPe Admin<br />Dashboard
                </h1>
                <p className="text-white/90 font-gilroy-medium text-lg max-w-md">
                  Manage your business effortlessly with our intuitive and powerful dashboard.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-gilroy-medium">Secure & Fast</h3>
                    <p className="text-white/70 text-sm">OTP-based authentication system</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                      <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-gilroy-medium">Real-time Analytics</h3>
                    <p className="text-white/70 text-sm">Monitor all business activities instantly</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white font-gilroy-medium">Complete Management</h3>
                    <p className="text-white/70 text-sm">Comprehensive tools for your business</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - Form */}
        <div className="w-full lg:w-1/2 px-6 py-10 sm:px-10 lg:px-16">
          {/* Logo for mobile */}
          <div className="lg:hidden flex justify-center mb-10">
            <img 
              src={Logo} 
              alt="CleverPe Logo" 
              className="h-12"
            />
          </div>
          
          <div className="max-w-md mx-auto">
            <h2 className="text-3xl font-gilroy-bold text-brand-dark mb-2 text-center lg:text-left">
              {step === 'phone' ? 'Welcome Back' : 'Verify OTP'}
            </h2>
            <p className="text-brand-gray font-gilroy-regular mb-8 text-center lg:text-left">
              {step === 'phone' 
                ? 'Sign in to CleverPe Admin Dashboard' 
                : `We've sent a verification code to ${maskedPhone}`}
            </p>
            
            {step === 'phone' ? (
              <form className="space-y-6" onSubmit={handlePhoneSubmit(handleSendOtp)}>
                <div className="space-y-1.5">
                  <label htmlFor="phoneNumber" className="block text-sm font-gilroy-medium text-brand-dark">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                      <span className="text-brand-gray/70 font-gilroy-medium">+91</span>
                    </div>
                    <input
                      id="phoneNumber"
                      type="tel"
                      className={`block w-full rounded-xl border-0 py-4 pl-14 pr-4 bg-[#F8FAFF] text-brand-dark text-lg font-gilroy-medium shadow-sm ring-1 ring-inset 
                        ${phoneErrors.phoneNumber ? 'ring-red-300' : 'ring-[#E3E8FF]'} 
                        placeholder:text-brand-gray/40 focus:ring-2 focus:ring-inset focus:ring-brand-blue focus:bg-white transition-all duration-200`}
                      placeholder="Enter mobile number"
                      {...registerPhone('phoneNumber', {
                        required: 'Mobile number is required',
                        validate: {
                          validNumber: (value) => isValidMobileNumber(value) || 'Please enter a valid 10-digit mobile number'
                        }
                      })}
                    />
                    {/* Decorative dot pattern */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-30">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="4" cy="4" r="1.5" fill="#5865F2" />
                        <circle cx="4" cy="12" r="1.5" fill="#5865F2" />
                        <circle cx="4" cy="20" r="1.5" fill="#5865F2" />
                        <circle cx="12" cy="4" r="1.5" fill="#5865F2" />
                        <circle cx="12" cy="12" r="1.5" fill="#5865F2" />
                        <circle cx="12" cy="20" r="1.5" fill="#5865F2" />
                        <circle cx="20" cy="4" r="1.5" fill="#5865F2" />
                        <circle cx="20" cy="12" r="1.5" fill="#5865F2" />
                        <circle cx="20" cy="20" r="1.5" fill="#5865F2" />
                      </svg>
                    </div>
                  </div>
                  {phoneErrors.phoneNumber && (
                    <p className="mt-1.5 text-sm text-red-500 font-gilroy-medium">{phoneErrors.phoneNumber.message}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative flex w-full justify-center items-center rounded-xl bg-gradient-to-r from-gradient-blue-1 to-gradient-blue-2 py-4 px-6 text-base font-gilroy-semibold text-white shadow-lg shadow-brand-blue/20 hover:shadow-xl hover:shadow-brand-blue/30 transform hover:-translate-y-0.5 transition-all duration-200
                    ${loading ? 'opacity-80 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Send OTP
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form className="space-y-6" onSubmit={handleOtpSubmit(handleVerifyOtp)}>
                {error && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-600">
                    <p className="font-gilroy-medium">{error}</p>
                  </div>
                )}
                
                <div className="space-y-1.5">
                  <label htmlFor="otp" className="block text-sm font-gilroy-medium text-brand-dark">
                    Enter 6-digit OTP
                  </label>
                  <div className="relative">
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      autoComplete="one-time-code"
                      onKeyDown={handleOtpInput}
                      onPaste={handleOtpPaste}
                      className={`block w-full rounded-xl border-0 py-5 px-4 text-center text-xl font-gilroy-bold tracking-[0.5em] bg-[#F8FAFF] text-brand-dark shadow-sm ring-1 ring-inset 
                        ${otpErrors.otp ? 'ring-red-300' : 'ring-[#E3E8FF]'} 
                        placeholder:text-brand-gray/40 focus:ring-2 focus:ring-inset focus:ring-brand-blue focus:bg-white transition-all duration-200`}
                      placeholder="• • • • • •"
                      {...registerOtp('otp', {
                        required: 'OTP is required',
                        pattern: {
                          value: /^\d{6}$/,
                          message: 'Please enter a valid 6-digit OTP'
                        }
                      })}
                    />
                    
                    {/* Animated pulse dots */}
                    <div className="flex justify-center space-x-1 mt-3">
                      {[...Array(6)].map((_, i) => (
                        <div 
                          key={i} 
                          className="h-1 w-8 rounded-full bg-gradient-blue-1 opacity-20"
                        />
                      ))}
                    </div>
                  </div>
                  
                  {otpErrors.otp && (
                    <p className="mt-1.5 text-sm text-red-500 font-gilroy-medium">{otpErrors.otp.message}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative flex w-full justify-center items-center rounded-xl bg-gradient-to-r from-gradient-blue-1 to-gradient-blue-2 py-4 px-6 text-base font-gilroy-semibold text-white shadow-lg shadow-brand-blue/20 hover:shadow-xl hover:shadow-brand-blue/30 transform hover:-translate-y-0.5 transition-all duration-200
                    ${loading ? 'opacity-80 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify & Login
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
                
                <div className="flex justify-between items-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('phone');
                      // This will trigger the expireOTP effect
                    }}
                    className="text-sm font-gilroy-medium text-brand-blue hover:text-gradient-blue-1 flex items-center transition-colors duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                  </button>
                  
                  <div className="flex items-center">
                    {resendTimer > 0 && (
                      <span className="text-sm font-gilroy-medium text-brand-gray mr-2">
                        Resend in {formatTime(resendTimer)}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        // Try to get mobile number from state or sessionStorage
                        const currentMobileNo = mobileNo || sessionStorage.getItem('mobileNo');
                        
                        console.log('[debug] Resending OTP for:', currentMobileNo);
                        
                        if (!currentMobileNo) {
                          console.error('[debug] Mobile number is missing for resend');
                          toast.error('Mobile number is missing');
                          setStep('phone');
                          return;
                        }
                        
                        // Don't expire previous OTP here as it causes issues
                        // Instead, the sendOTP action will naturally replace it
                        
                        const result = await dispatch(sendOTP(currentMobileNo));
                        
                        if (sendOTP.fulfilled.match(result)) {
                          console.log('[debug] OTP resent successfully');
                          setResendTimer(180); // Reset timer to 3 minutes
                          setCanResend(false);
                          toast.success('OTP resent successfully!');
                        } else {
                          console.error('[debug] Failed to resend OTP:', result.error);
                        }
                      }}
                      disabled={loading || !canResend}
                      className={`text-sm font-gilroy-medium transition-colors duration-200 ${
                        canResend 
                          ? 'text-brand-blue hover:text-gradient-blue-1' 
                          : 'text-brand-gray/50 cursor-not-allowed'
                      }`}
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              </form>
            )}
            
            {/* Extra information */}
            <div className="mt-12 pt-8 border-t border-[#E3E8FF]">
              <div className="flex items-center justify-center space-x-3">
                <div className="h-9 w-9 rounded-full bg-[#F8FAFF] flex items-center justify-center border border-[#E3E8FF]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-brand-gray">
                  For any technical issues, please contact our support team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};